/**
 * Lightweight client-side CSV export. Builds a BOM-prefixed UTF-8 string and
 * triggers a download via an in-memory blob — no backend round-trip.
 *
 * Each cell is escaped: quotes doubled, then wrapped in quotes if it contains
 * comma / quote / newline. Numbers and nulls handled.
 */
type CsvValue = string | number | boolean | null | undefined;

function escapeCsv(value: CsvValue): string {
    if (value == null) return '';
    let s = String(value);
    if (s.includes('"')) s = s.replace(/"/g, '""');
    if (/[",\n\r]/.test(s)) s = `"${s}"`;
    return s;
}

export interface CsvColumn<T> {
    header: string;
    accessor: (row: T) => CsvValue;
}

export function downloadCsv<T>(filename: string, columns: CsvColumn<T>[], rows: T[]): void {
    if (typeof window === 'undefined') return;
    const headerLine = columns.map((c) => escapeCsv(c.header)).join(',');
    const dataLines = rows.map((row) =>
        columns.map((c) => escapeCsv(c.accessor(row))).join(','),
    );
    // BOM lets Excel detect UTF-8 properly when opening directly.
    const csv = '﻿' + [headerLine, ...dataLines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** YYYY-MM-DD slug used in default filenames. */
export function todayYmd(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
